import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-hot-toast";
import usePaymentTransactionsByStation from "../../hooks/usePaymentTransactionsByStation";
import { exportToCSV } from "../../utils/exportData";
import { formatINR } from "../../utils/formatINR";

const PaymentTransactionsDesktop = ({ stationId, range }) => {
  const { transactions, isLoading, error, message } = usePaymentTransactionsByStation(stationId, range);
  const [localTransactions, setLocalTransactions] = useState([]);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState("all");
  const [vehicleIdSearch, setVehicleIdSearch] = useState("");

  const handleExport = () => {
    // Export filtered data instead of all transactions
    exportToCSV(filteredTransactions, `payment_transactions_${stationId}`);
  };

  // Show server messages
  useEffect(() => {
    if (message) toast.success(message);
  }, [message]);

  // Clean nullable fields from backend
  useEffect(() => {
    if (transactions) {
      const cleaned = transactions.map((t) => ({
        ...t,
        id: t.id ?? "",
        user_id: t.user_id?.String ?? "",
        charging_session_id: t.charging_session_id?.String ?? "",
        payment_method_type: t.payment_method_type ?? "",
        ccs2_vehicle_id: t.ccs2_vehicle_id?.String ?? "",
        station_id: t.station_id?.String ?? "",
        gun_id: t.gun_id?.String ?? "",
        transaction_type: t.transaction_type ?? "",
        amount: t.amount ?? 0,
        gst_amount: t.gst_amount ?? 0,
        final_cost: t.final_cost ?? 0,
        status: t.status ?? "",
        payment_reference_id: t.payment_reference_id?.String ?? "",
        description: t.description?.String ?? "",
        created_at: t.created_at ? new Date(t.created_at).toLocaleString() : "",
        modified_at: t.modified_at ? new Date(t.modified_at).toLocaleString() : "",
        actions: t.actions ?? [],
      }));
      setLocalTransactions(cleaned);
    }
  }, [transactions]);

  // Get unique transaction types for filter dropdown
  const transactionTypes = useMemo(() => {
    const types = [...new Set(localTransactions.map(t => t.transaction_type).filter(Boolean))];
    return types.sort();
  }, [localTransactions]);

  // Filter transactions based on transaction type and vehicle ID search
  const filteredTransactions = useMemo(() => {
    return localTransactions.filter(transaction => {
      const matchesTransactionType = transactionTypeFilter === "all" || 
        transaction.transaction_type.toLowerCase() === transactionTypeFilter.toLowerCase();
      
      const matchesVehicleId = vehicleIdSearch === "" || 
        transaction.ccs2_vehicle_id.toLowerCase().includes(vehicleIdSearch.toLowerCase());
      
      return matchesTransactionType && matchesVehicleId;
    });
  }, [localTransactions, transactionTypeFilter, vehicleIdSearch]);

  const handleClearFilters = () => {
    setTransactionTypeFilter("all");
    setVehicleIdSearch("");
  };

  const getStatusBadge = (status) => {
    const lower = status.toLowerCase();
    const statusMap = {
      completed: { text: "Completed", color: "green" },
      pending: { text: "Pending", color: "yellow" },
      captured: { text: "Captured", color: "red" },
      processed: { text: "Processed", color: "gray" },
    };
    const s = statusMap[lower] || { text: status, color: "gray" };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${s.color}-50 text-${s.color}-700`}>
        <span className={`w-2 h-2 bg-${s.color}-700 rounded-full mr-1`}></span>
        {s.text}
      </span>
    );
  };

  const getTransactionBadge = (status) => {
    const lower = status.toLowerCase();
    const statusMap = {
      refund: { text: "Refund", color: "red" },
      qr_topup: { text: "Qr Topup", color: "yellow" },
      charging: { text: "Charging", color: "green" },
      processed: { text: "Processed", color: "gray" },
    };
    const s = statusMap[lower] || { text: status, color: "gray" };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${s.color}-50 text-${s.color}-700`}>
        {/* <span className={`w-2 h-2 bg-${s.color}-700 rounded-full mr-1`}></span> */}
        {s.text}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <header className="bg-white border-b px-4 py-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
          <h1 className="text-xl font-bold text-gray-900">Payment Transactions for Station {stationId}</h1>
          <button
            onClick={handleExport}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium whitespace-nowrap"
          >
            Export Data
          </button>
        </div>
        
        {/* Filters Section */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          {/* Transaction Type Filter */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label htmlFor="transactionType" className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Transaction Type:
            </label>
            <select
              id="transactionType"
              value={transactionTypeFilter}
              onChange={(e) => setTransactionTypeFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              {transactionTypes.map(type => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Vehicle ID Search */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label htmlFor="vehicleSearch" className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Vehicle ID:
            </label>
            <input
              id="vehicleSearch"
              type="text"
              placeholder="Search by Vehicle ID..."
              value={vehicleIdSearch}
              onChange={(e) => setVehicleIdSearch(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Clear Filters Button */}
          {(transactionTypeFilter !== "all" || vehicleIdSearch !== "") && (
            <button
              onClick={handleClearFilters}
              className="bg-gray-100 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Results Count */}
        <div className="mt-3 text-sm text-gray-600">
          Showing {filteredTransactions.length} of {localTransactions.length} transactions
        </div>
      </header>

      <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {[
                "Transaction ID", "Amount", "GST", "Final Cost", "Status", "Transaction Type",
                "Station ID", "Gun ID", "Vehicle ID", "Payment Method",
                "Description", "Payment Ref", "Date", "Actions"
              ].map((col) => (
                <th
                  key={col}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {filteredTransactions.map((t, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{t.id}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatINR(t.amount)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatINR(t.gst_amount)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatINR(t.final_cost)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">{getStatusBadge(t.status)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">{getTransactionBadge(t.transaction_type)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.station_id}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.gun_id}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.ccs2_vehicle_id}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.payment_method_type}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.description}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.payment_reference_id}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.created_at}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  {t.actions.map((action, j) => (
                    <button
                      key={j}
                      className={`${j > 0 ? "ml-4" : ""} ${
                        action === "Retry"
                          ? "text-red-600 hover:text-red-900"
                          : action === "View"
                          ? "text-blue-600 hover:text-blue-900"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      {action}
                    </button>
                  ))}
                </td>
              </tr>
            ))}

            {!isLoading && !error && filteredTransactions.length === 0 && localTransactions.length > 0 && (
              <tr>
                <td colSpan="14" className="px-6 py-4 text-center text-sm text-gray-500">
                  No transactions match the current filters
                </td>
              </tr>
            )}

            {!isLoading && !error && localTransactions.length === 0 && (
              <tr>
                <td colSpan="14" className="px-6 py-4 text-center text-sm text-gray-500">
                  No transactions found
                </td>
              </tr>
            )}

            {isLoading && (
              <tr>
                <td colSpan="14" className="px-6 py-4 text-center text-sm text-gray-500">
                  Loading...
                </td>
              </tr>
            )}

            {/* {error && (
              <tr>
                <td colSpan="14" className="px-6 py-4 text-center text-sm text-red-500">
                  {error}
                </td>
              </tr>
            )} */}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentTransactionsDesktop;